import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { studioBooks } from '@/lib/api';

interface CheckoutFormProps {
  onSuccess: () => void;
}

function CheckoutForm({ onSuccess }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError) throw new Error(pmError.message);
      if (!paymentMethod) throw new Error('Failed to create payment method');

      await studioBooks.subscribe({ payment_method_id: paymentMethod.id });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      <div className="border border-gray-200 rounded-lg p-3">
        <CardElement
          options={{
            style: {
              base: { fontSize: '14px', color: '#374151', '::placeholder': { color: '#9CA3AF' } },
            },
          }}
        />
      </div>

      {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      <button
        type="submit"
        disabled={loading || !stripe}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
      >
        {loading ? 'Processing…' : 'Start Building — $199/mo'}
      </button>

      <p className="text-center text-xs text-gray-400">
        Powered by <span className="font-medium">Stripe</span>. Cancel anytime.
      </p>
    </form>
  );
}

export function StudioSubscribe() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [initLoading, setInitLoading] = useState(true);

  useEffect(() => {
    async function initBooksFlow() {
      try {
        const result = await studioBooks.setupIntent();
        setStripePromise(loadStripe(result.publishableKey));
        setClientSecret(result.clientSecret);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load payment form. Please try again.');
      } finally {
        setInitLoading(false);
      }
    }
    void initBooksFlow();
  }, []);

  useEffect(() => {
    if (subscribed) {
      const t = setTimeout(() => navigate('/onboarding'), 1500);
      return () => clearTimeout(t);
    }
  }, [subscribed, navigate]);

  const features = [
    'Unlimited app builds (pay per deployed app)',
    'The One Security built in — no extra charge',
    'PSA data bridge (tickets, assets, billing)',
    'AI build assistant with token budget',
    '10 pre-built starter templates',
    'White-label with your branding',
    '$50 AI usage credits included monthly',
    'Client tenant isolation & MFA',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">The One AI Studio</h1>
          <p className="text-blue-200 text-lg">Build client-facing apps for your MSP in a day</p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <div className="mb-6">
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-bold text-gray-900">$199</span>
              <span className="text-gray-500">/month platform fee</span>
            </div>
            <p className="text-sm text-gray-500">+ $29/app/month + $5/client/month</p>
          </div>

          <ul className="space-y-3 mb-8">
            {features.map((feature) => (
              <li key={feature} className="flex items-center gap-3">
                <span className="text-green-500 flex-shrink-0">✓</span>
                <span className="text-gray-700 text-sm">{feature}</span>
              </li>
            ))}
          </ul>

          {subscribed ? (
            <div className="text-center py-4">
              <p className="text-green-600 font-semibold mb-2">Subscribed! Setting up your Studio…</p>
            </div>
          ) : initLoading ? (
            <div className="h-24 animate-pulse bg-gray-100 rounded-lg" />
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm text-center">{error}</div>
          ) : stripePromise && clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm onSuccess={() => setSubscribed(true)} />
            </Elements>
          ) : null}
        </div>
      </div>
    </div>
  );
}
